import ExpandMore from "@mui/icons-material/ExpandMore";
import Send from "@mui/icons-material/Send";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useRef, useState } from "react";
import { useToken } from "../../server/auth/use-token";
import { authHeader } from "../../utils/auth-header";
import {
  AiImageStyleConfig,
  AiImageStyleToggle,
} from "../brand-kit/components/ai-image-style-toggle";
import {
  trackContentGeneration,
  trackContentGenerationResult,
} from "../content-generation/track-content-generation";
import { selfDriving } from "../self-driving/self-driving";
import { AiImageResultsGrid } from "./ai-image-results-grid";
import { ReferenceImagesInput } from "./reference-images-input";

import type { AiImageStyle } from "../brand-kit/components/ai-image-style-toggle";
import type { BrandInfoSelected } from "../brand-kit/types";
import type { FC } from "react";

const NUM_IMAGES = 4;
const MIN_IMAGE_SIZE_BYTES = 1000;
const MIN_IMAGE_DIMENSION = 2;

export const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png"];
export const MAX_REFERENCE_IMAGES = 3;
export const MAX_REFERENCE_IMAGE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB

export interface ReferenceImage {
  url: string;
  content_type: string;
}

export interface ImageResult {
  blobUrl: string | null;
  loading: boolean;
  error: boolean;
}

const validateAndCreateBlobUrl = async (url: string): Promise<string> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${response.status}`);
  }

  const blob = await response.blob();

  if (!blob.type.startsWith("image/") || blob.size < MIN_IMAGE_SIZE_BYTES) {
    throw new Error("Invalid image");
  }

  const blobUrl = URL.createObjectURL(blob);

  await new Promise<void>((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth >= MIN_IMAGE_DIMENSION && img.naturalHeight >= MIN_IMAGE_DIMENSION) {
        resolve();
      } else {
        URL.revokeObjectURL(blobUrl);
        reject(new Error("Image has invalid dimensions"));
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(blobUrl);
      reject(new Error("Image failed to load"));
    };
    img.src = blobUrl;
  });

  return url;
};

export const AiTab: FC<{
  onImageClick?: (imageUrl: string) => void;
  selectedImage: string | null;
  onSelectionChange?: (hasSelection: boolean) => void;
  hidden?: boolean;
  branding?: BrandInfoSelected;
}> = ({ onImageClick, selectedImage, onSelectionChange, hidden = false, branding }) => {
  const getToken = useToken();
  const [query, setQuery] = useState("");
  const [imageResults, setImageResults] = useState<ImageResult[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState(branding?.ai_image_style ?? "illustration");
  const abortControllerRef = useRef<AbortController | null>(null);
  const blobUrlsRef = useRef<string[]>([]);
  const [referenceImages, setReferenceImages] = useState<ReferenceImage[]>([]);
  const [uploadingReferenceImage, setUploadingReferenceImage] = useState(false);

  const handleImageClick = (url: string): void => {
    onImageClick?.(url);
    onSelectionChange?.(true);
  };

  const cleanupBlobUrls = (): void => {
    blobUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    blobUrlsRef.current = [];
  };

  const handleSearch = useCallback(async () => {
    const trimmedQuery = query.trim();

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    cleanupBlobUrls();

    if (!trimmedQuery) {
      setImageResults([]);
      setHasSearched(false);
      return;
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const initialResults: ImageResult[] = Array.from({ length: NUM_IMAGES }, () => ({
      blobUrl: null,
      loading: true,
      error: false,
    }));
    setImageResults(initialResults);
    setHasSearched(true);

    const token = await getToken();

    const contentGenerationId = await trackContentGeneration(token, {
      contentType: "mfai",
      prompt: trimmedQuery,
    });

    const requests = Array.from({ length: NUM_IMAGES }, (_, index) =>
      (async () => {
        try {
          const { data, error } = await selfDriving.POST("/photos/ai", {
            body: {
              prompt: trimmedQuery,
              style: selectedStyle,
              images: referenceImages,
            },
            ...authHeader(token),
            signal: abortController.signal,
          });

          if (abortController.signal.aborted) return;

          if (error || !data?.url) {
            setImageResults((prev) => {
              const updated = [...prev];
              updated[index] = { blobUrl: null, loading: false, error: true };
              return updated;
            });
            return;
          }

          try {
            const blobUrl = await validateAndCreateBlobUrl(data.url);

            if (abortController.signal.aborted) {
              URL.revokeObjectURL(blobUrl);
              return;
            }

            blobUrlsRef.current.push(blobUrl);

            if (contentGenerationId) {
              void trackContentGenerationResult(token, contentGenerationId, { url: data.url });
            }

            setImageResults((prev) => {
              const updated = [...prev];
              updated[index] = { blobUrl, loading: false, error: false };
              return updated;
            });
          } catch {
            if (abortController.signal.aborted) return;

            setImageResults((prev) => {
              const updated = [...prev];
              updated[index] = { blobUrl: null, loading: false, error: true };
              return updated;
            });
          }
        } catch (error) {
          if (error instanceof Error && error.name === "AbortError") {
            return;
          }
          console.error(`AI image generation failed for index ${index}:`, error);
          setImageResults((prev) => {
            const updated = [...prev];
            updated[index] = { blobUrl: null, loading: false, error: true };
            return updated;
          });
        }
      })(),
    );

    await Promise.allSettled(requests);
  }, [getToken, query, referenceImages, selectedStyle]);

  return (
    <Box
      sx={{
        flex: 1,
        overflowY: "auto",
        px: 3,
        py: 2.5,
        backgroundColor: "background.default",
        display: hidden ? "none" : "block",
      }}
    >
      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        Unique images. Can take more time to generate.
      </Typography>

      <>
        <Stack direction="row" sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mr: 2 }}>
            Choose an image style
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {/* oxlint-disable-next-line typescript/no-unsafe-type-assertion */}
            {AiImageStyleConfig[selectedStyle as AiImageStyle]?.name}
          </Typography>
        </Stack>
        <Box sx={{ mb: 2 }}>
          <AiImageStyleToggle aiImageStyle={selectedStyle} onChange={(v) => setSelectedStyle(v)} />
        </Box>
      </>

      <Divider sx={{ my: 2 }} />

      <Accordion
        defaultExpanded
        disableGutters
        elevation={0}
        sx={{
          my: 2,
          backgroundColor: "background.default",
          "&:before": { display: "none" },
          border: "none",
          boxShadow: "none",
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            px: 0,
            "& .MuiAccordionSummary-content": { my: 0 },
          }}
        >
          <Typography variant="subtitle2" fontWeight={500} fontSize="14px">
            Customise image prompt
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0 }}>
          <TextField
            fullWidth
            placeholder="Describe the image you want"
            multiline
            minRows={3}
            maxRows={8}
            value={query}
            label="Image prompt"
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void handleSearch();
              }
            }}
            sx={{ backgroundColor: "white", borderRadius: 2 }}
            slotProps={{
              input: {
                sx: {
                  position: "relative",
                  alignItems: "flex-start",
                  pb: referenceImages.length > 0 || uploadingReferenceImage ? "6rem" : "2.5rem",
                },
                endAdornment: (
                  <>
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 8,
                        left: 14,
                      }}
                    >
                      <ReferenceImagesInput
                        referenceImages={referenceImages}
                        onReferenceImagesChange={setReferenceImages}
                        uploadingReferenceImage={uploadingReferenceImage}
                        onUploadingChange={setUploadingReferenceImage}
                      />
                    </Box>
                    <IconButton
                      data-analytics-id="ai-tab-search-button"
                      onClick={() => void handleSearch()}
                      size="small"
                      disabled={!query.trim()}
                      sx={{
                        position: "absolute",
                        bottom: 8,
                        right: 8,
                        color: "primary.main",
                      }}
                    >
                      <Send />
                    </IconButton>
                  </>
                ),
              },
            }}
          />
        </AccordionDetails>
      </Accordion>

      <Divider sx={{ my: 2 }} />

      <AiImageResultsGrid
        imageResults={imageResults}
        selectedImage={selectedImage}
        onImageClick={handleImageClick}
        hasSearched={hasSearched}
      />
    </Box>
  );
};
